/**
 * TopBar primitive.
 *
 * A thin glass bar (tier1) that floats above the page content:
 *   - Left: breadcrumb derived from the current pathname
 *   - Center: optional ticker context pill (for ticker-scoped routes)
 *   - Right: compact user menu
 *
 * Collapses on scroll-down (> 80px from top) and reappears on scroll-up.
 * Respects prefers-reduced-motion via useReducedMotion().
 *
 * Do not add em-dashes anywhere.
 */

import type { CSSProperties } from 'react'
import { useRef } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { motion, useReducedMotion, useScroll, useMotionValueEvent } from 'framer-motion'
import { useState } from 'react'
import { ChevronRight, User, LogOut, Home } from 'lucide-react'
import { elevation, z, type as typeTokens, pillarAccent, color, motion as motionTokens } from '@/design-system/tokens'
import { useAuth } from '@/contexts/AuthContext'
import { getPillarForRoute } from './pillar'

// ---------------------------------------------------------------------------
// Route-title map for breadcrumb labels
// ---------------------------------------------------------------------------
const ROUTE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/screen': 'Screen',
  '/compare': 'Compare',
  '/manual-input': 'Manual Input',
  '/mna': 'M&A Analyzer',
  '/watchlist': 'Watchlist',
  '/events': 'Events',
  '/portfolio': 'Portfolio',
  '/estimates': 'Estimates',
  '/backtesting': 'Track Record',
  '/factors': 'Factor Analytics',
  '/ml-insights': 'ML Insights',
  '/strategies': 'Strategies',
  '/moat': 'Moat Analysis',
  '/credit-universe': 'Credit Universe',
  '/crowded-trades': 'Crowded Trades',
  '/batch': 'Batch Analysis',
  '/calibration': 'Calibration',
  '/apophis': 'Apophis',
  '/apophis/history': 'Apophis Documents',
  '/apophis/documents': 'Apophis Documents',
  '/compliance': 'Compliance',
  '/analytics': 'Analytics',
  '/devtools': 'Dev Tools',
  '/settings': 'Settings',
  '/profile': 'Profile',
}

// Segments that appear in dynamic paths (e.g. /analysis/AAPL -> Analysis)
const SEGMENT_LABELS: Record<string, string> = {
  analysis: 'Analysis',
  dashboard: 'Dashboard',
  research: 'Research',
  moat: 'Moat',
  'mna': 'M&A',
  jobs: 'Job Progress',
  quantitative: 'Quantitative',
  'credit-risk': 'Credit Risk',
  'insider-flow': 'Insider Flow',
  governance: 'Governance',
  'event-timeline': 'Event Timeline',
  'options-market': 'Options Market',
  'macro-overlay': 'Macro Overlay',
  'attention-sentiment': 'Attention & Sentiment',
  input: 'Input',
  deliverable: 'Deliverable',
}

interface BreadcrumbSegment {
  label: string
  /** Optional: if present, clicking navigates there. */
  href?: string
}

function buildBreadcrumbs(pathname: string): BreadcrumbSegment[] {
  // Exact-match first
  if (ROUTE_TITLES[pathname]) {
    return [{ label: ROUTE_TITLES[pathname] }]
  }

  const parts = pathname.split('/').filter(Boolean)
  const crumbs: BreadcrumbSegment[] = []

  for (let i = 0; i < parts.length; i++) {
    const segment = parts[i]
    const upTo = '/' + parts.slice(0, i + 1).join('/')
    const label = SEGMENT_LABELS[segment] ?? segment.toUpperCase()
    crumbs.push({
      label,
      href: i < parts.length - 1 ? upTo : undefined,
    })
  }

  return crumbs
}

/** Returns true when the pathname is ticker-scoped (has a dynamic :ticker segment). */
function extractTickerFromPath(pathname: string): string | null {
  const analysisMatch = pathname.match(/^\/analysis\/([A-Z0-9.]{1,10})(?:\/|$)/i)
  if (analysisMatch) return analysisMatch[1].toUpperCase()

  const dashMatch = pathname.match(/^\/dashboard\/([A-Z0-9.]{1,10})(?:\/|$)/i)
  if (dashMatch) return dashMatch[1].toUpperCase()

  const moatMatch = pathname.match(/^\/moat\/([A-Z0-9.]{1,10})(?:\/|$)/i)
  if (moatMatch) return moatMatch[1].toUpperCase()

  return null
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Breadcrumb({ segments }: { segments: BreadcrumbSegment[] }) {
  const navigate = useNavigate()
  const labelStyle: CSSProperties = {
    fontSize: `${typeTokens.caption.size}px`,
    fontWeight: typeTokens.caption.weight,
    letterSpacing: typeTokens.caption.tracking,
    color: color.graySecondary,
    whiteSpace: 'nowrap',
  }
  const linkStyle: CSSProperties = {
    ...labelStyle,
    color: color.grayPrimary,
    cursor: 'pointer',
    textDecoration: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
  }

  // Breadcrumb as an ordered list so screen readers announce item count and
  // position (WCAG 2.1 SC 1.3.1 + ARIA APG). Native list semantics are kept;
  // list-style is suppressed visually.
  return (
    <nav
      aria-label="Breadcrumb"
      style={{ minWidth: 0 }}
    >
      <ol
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          minWidth: 0,
          listStyle: 'none',
          padding: 0,
          margin: 0,
        }}
      >
        {segments.map((seg, idx) => (
          <li
            key={idx}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}
          >
            {idx > 0 && (
              <ChevronRight
                aria-hidden="true"
                style={{ width: '12px', height: '12px', color: color.graySecondary, flexShrink: 0 }}
              />
            )}
            {seg.href ? (
              <button
                onClick={() => navigate(seg.href!)}
                style={linkStyle}
              >
                {seg.label}
              </button>
            ) : (
              <span style={labelStyle} aria-current="page">
                {seg.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}

function TickerPill({ ticker, accent }: { ticker: string; accent: string }) {
  const pillStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: '9999px',
    border: `1px solid ${hexToRgba(accent, 0.4)}`,
    background: hexToRgba(accent, 0.08),
    fontSize: `${typeTokens.caption.size}px`,
    fontWeight: 600,
    letterSpacing: '0.08em',
    color: accent,
  }
  return <span style={pillStyle}>{ticker}</span>
}

function UserMenu() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)

  if (!user) return null

  const avatarStyle: CSSProperties = {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: hexToRgba(color.gold, 0.15),
    border: `1px solid ${hexToRgba(color.gold, 0.4)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  }

  const menuStyle: CSSProperties = {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 8px)',
    minWidth: '180px',
    ...elevation.tier3,
    borderRadius: '8px',
    zIndex: z.dropdown,
    padding: '4px',
  }

  const menuItemStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: `${typeTokens.bodySmall.size}px`,
    color: color.graySecondary,
    cursor: 'pointer',
    width: '100%',
    background: 'transparent',
    border: 'none',
    textAlign: 'left',
  }

  const triggerId = 'topbar-user-menu-trigger'

  return (
    <div style={{ position: 'relative' }}>
      <button
        id={triggerId}
        style={avatarStyle}
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="User menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <User style={{ width: '12px', height: '12px', color: color.gold }} />
      </button>
      {menuOpen && (
        <>
          {/* Click-away overlay */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: z.dropdown - 1 }}
            onClick={() => setMenuOpen(false)}
          />
          <div style={menuStyle} role="menu" aria-labelledby={triggerId}>
            <div style={{ padding: '8px 12px 6px', borderBottom: `1px solid rgba(255,255,255,0.06)`, marginBottom: '4px' }}>
              <p style={{ fontSize: `${typeTokens.bodySmall.size}px`, color: color.whiteSoft, margin: 0, marginBottom: '2px' }}>
                {user.name ?? 'User'}
              </p>
              <p style={{ fontSize: `${typeTokens.caption.size}px`, color: color.graySecondary, margin: 0 }}>
                {user.email}
              </p>
            </div>
            <button
              role="menuitem"
              style={menuItemStyle}
              onClick={() => { setMenuOpen(false); navigate('/profile') }}
            >
              <User style={{ width: '12px', height: '12px' }} />
              Profile
            </button>
            <button
              role="menuitem"
              style={{ ...menuItemStyle, color: color.destructive }}
              onClick={async () => {
                setMenuOpen(false)
                await logout()
                navigate('/login')
              }}
            >
              <LogOut style={{ width: '12px', height: '12px' }} />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// TopBar
// ---------------------------------------------------------------------------

export interface TopBarProps {
  /** Optional override for the center ticker pill */
  ticker?: string
  /**
   * Scroll container to observe for the scroll-collapse behavior. AppLayout
   * puts its content inside a scrollable <main>, so the window never
   * scrolls. Callers pass a ref to the scroll root so useScroll tracks the
   * right element. If omitted, falls back to window scroll (rarely correct).
   */
  scrollContainer?: React.RefObject<HTMLElement | null>
}

export function TopBar({ ticker: tickerProp, scrollContainer }: TopBarProps = {}) {
  const { pathname } = useLocation()
  const params = useParams<{ ticker?: string }>()
  const navigate = useNavigate()
  const shouldReduceMotion = useReducedMotion()

  const pillar = getPillarForRoute(pathname)
  const accent = pillarAccent[pillar]

  const resolvedTicker = tickerProp ?? params.ticker?.toUpperCase() ?? extractTickerFromPath(pathname)

  const segments = buildBreadcrumbs(pathname)

  // Scroll collapse logic. Observe the scroll container if provided so
  // collapse actually fires in AppLayout (where <main> is the scroll root).
  const [hidden, setHidden] = useState(false)
  const lastScrollY = useRef(0)
  const { scrollY } = useScroll(scrollContainer ? { container: scrollContainer } : undefined)

  useMotionValueEvent(scrollY, 'change', (current) => {
    if (shouldReduceMotion) return
    const diff = current - lastScrollY.current
    if (current > 80 && diff > 0) {
      setHidden(true)
    } else if (diff < 0) {
      setHidden(false)
    }
    lastScrollY.current = current
  })

  const barStyle: CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: z.topbar,
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '24px',
    paddingRight: '24px',
    gap: '16px',
    ...elevation.tier1,
    borderBottom: elevation.tier1.border,
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
  }

  // Compute effective hidden + pointer-event suppression together so the
  // collapsed bar cannot eat clicks on content behind it.
  const effectivelyHidden = !shouldReduceMotion && hidden
  const motionStyle: CSSProperties = {
    ...barStyle,
    pointerEvents: effectivelyHidden ? 'none' : undefined,
  }

  return (
    <motion.div
      data-slot="topbar"
      style={motionStyle as never}
      aria-hidden={effectivelyHidden || undefined}
      animate={shouldReduceMotion ? {} : { y: hidden ? -48 : 0, opacity: hidden ? 0 : 1 }}
      transition={
        shouldReduceMotion
          ? { duration: 0 }
          : {
              duration: motionTokens.duration.fast / 1000,
              ease: motionTokens.ease.standard as [number, number, number, number],
            }
      }
    >
      {/* Left: breadcrumb */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Breadcrumb segments={segments} />
      </div>

      {/* Center: ticker pill */}
      {resolvedTicker && (
        <div style={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <TickerPill ticker={resolvedTicker} accent={accent} />
        </div>
      )}

      {/* Right: user menu */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        <button
          onClick={() => navigate('/')}
          aria-label="Home"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: color.graySecondary,
          }}
        >
          <Home style={{ width: '14px', height: '14px' }} />
        </button>
        <UserMenu />
      </div>
    </motion.div>
  )
}

/** Inline hexToRgba to avoid glass module import */
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// Backward compat default export
export default TopBar
